import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { API_BASE_URL } from '../../utils/constants'
import {
  BookOpen,
  Key,
  Bot,
  Database,
  MessageCircle,
  Send,
  Users,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  ExternalLink,
  ArrowRight,
  Shield,
  AlertTriangle,
  FileText,
  Globe,
  Upload,
  GitBranch,
  Code
} from 'lucide-react'

// Reusable code block with copy button
function CodeBlock({ code, language = 'bash' }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="relative group">
      <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm leading-relaxed">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Copy to clipboard"
      >
        {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  )
}

// Collapsible section
function Section({
  title,
  icon: Icon,
  step,
  children,
  defaultOpen = false
}: {
  title: string
  icon: React.ElementType
  step?: number
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {step !== undefined && (
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 text-sm font-bold">
              {step}
            </span>
          )}
          <Icon className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        {open ? (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {open && <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">{children}</div>}
    </div>
  )
}

// UML sequence diagram — pure CSS/HTML with pixel layout
type SeqRow =
  | { type: 'arrow'; from: number; to: number; label: string; dashed?: boolean; color?: string }
  | { type: 'self'; col: number; label: string; color?: string }
  | { type: 'note'; col: number; span?: number; label: string; variant?: 'info' | 'error' | 'warning' | 'alt' }
  | { type: 'divider'; label: string; variant?: 'alt' | 'error' }

const COLORS: Record<string, string> = {
  blue: '#3b82f6', green: '#22c55e', purple: '#a855f7',
  orange: '#f97316', amber: '#f59e0b', gray: '#9ca3af', red: '#ef4444'
}

function SequenceDiagram({ actors, rows }: { actors: string[]; rows: SeqRow[] }) {
  const COL_W = 150          // px per actor column
  const ROW_H = 54           // px per message row
  const HEADER_H = 40
  const PAD_X = 20           // left/right padding
  const totalW = actors.length * COL_W + PAD_X * 2
  const bodyH = rows.length * ROW_H + 20

  // center-x of column i in pixels
  const cx = (i: number) => PAD_X + i * COL_W + COL_W / 2

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <div style={{ width: totalW, minWidth: '100%' }} className="relative">
        {/* Actor headers */}
        <div className="flex" style={{ height: HEADER_H, paddingLeft: PAD_X, paddingRight: PAD_X }}>
          {actors.map((name, i) => (
            <div key={i} className="flex items-center justify-center" style={{ width: COL_W }}>
              <div className="px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs font-bold text-blue-900 whitespace-nowrap">
                {name}
              </div>
            </div>
          ))}
        </div>

        {/* Body: lifelines + rows */}
        <div className="relative" style={{ height: bodyH }}>
          {/* Lifelines */}
          {actors.map((_, i) => (
            <div
              key={i}
              className="absolute top-0 bottom-0"
              style={{ left: cx(i), width: 0, borderLeft: '2px dashed #e5e7eb' }}
            />
          ))}

          {/* Rows */}
          {rows.map((row, ri) => {
            const y = ri * ROW_H + ROW_H / 2

            // === ARROW ===
            if (row.type === 'arrow') {
              const c = COLORS[row.color || 'blue']
              const x1 = cx(row.from)
              const x2 = cx(row.to)
              const goesRight = row.to > row.from
              const left = Math.min(x1, x2)
              const width = Math.abs(x2 - x1)
              const ARROW_SIZE = 7

              return (
                <div key={ri} className="absolute" style={{ top: y - ROW_H / 2, height: ROW_H, left: 0, right: 0 }}>
                  {/* Label above arrow */}
                  <div
                    className="absolute flex items-end justify-center"
                    style={{ left, width, top: 0, height: ROW_H / 2 - 4 }}
                  >
                    <span className="text-xs font-medium text-gray-700 bg-white px-1.5 leading-tight text-center">
                      {row.label}
                    </span>
                  </div>
                  {/* Arrow line */}
                  <div
                    className="absolute"
                    style={{
                      top: ROW_H / 2,
                      left: goesRight ? x1 : x2 + ARROW_SIZE,
                      width: width - ARROW_SIZE,
                      height: 0,
                      borderTop: `2px ${row.dashed ? 'dashed' : 'solid'} ${c}`,
                    }}
                  />
                  {/* Arrowhead */}
                  <div
                    className="absolute"
                    style={{
                      top: ROW_H / 2 - ARROW_SIZE / 2,
                      left: goesRight ? x2 - ARROW_SIZE : x2,
                      width: 0, height: 0,
                      borderTop: `${ARROW_SIZE / 2}px solid transparent`,
                      borderBottom: `${ARROW_SIZE / 2}px solid transparent`,
                      ...(goesRight
                        ? { borderLeft: `${ARROW_SIZE}px solid ${c}` }
                        : { borderRight: `${ARROW_SIZE}px solid ${c}` }),
                    }}
                  />
                </div>
              )
            }

            // === SELF CALL (loop) ===
            if (row.type === 'self') {
              const c = COLORS[row.color || 'gray']
              const x = cx(row.col)
              const loopW = 40
              const loopH = 20
              const ARROW_SIZE = 6

              return (
                <div key={ri} className="absolute" style={{ top: y - ROW_H / 2, height: ROW_H, left: 0, right: 0 }}>
                  {/* Top horizontal line going right */}
                  <div className="absolute" style={{ top: ROW_H / 2 - loopH / 2, left: x, width: loopW, height: 0, borderTop: `2px solid ${c}` }} />
                  {/* Vertical line going down */}
                  <div className="absolute" style={{ top: ROW_H / 2 - loopH / 2, left: x + loopW, width: 0, height: loopH, borderLeft: `2px solid ${c}` }} />
                  {/* Bottom horizontal line going back left */}
                  <div className="absolute" style={{ top: ROW_H / 2 + loopH / 2, left: x + ARROW_SIZE, width: loopW - ARROW_SIZE, height: 0, borderTop: `2px solid ${c}` }} />
                  {/* Arrowhead pointing left */}
                  <div className="absolute" style={{
                    top: ROW_H / 2 + loopH / 2 - ARROW_SIZE / 2,
                    left: x,
                    width: 0, height: 0,
                    borderTop: `${ARROW_SIZE / 2}px solid transparent`,
                    borderBottom: `${ARROW_SIZE / 2}px solid transparent`,
                    borderRight: `${ARROW_SIZE}px solid ${c}`,
                  }} />
                  {/* Label */}
                  <div
                    className="absolute text-xs font-medium text-gray-600 whitespace-nowrap"
                    style={{ top: ROW_H / 2 - loopH / 2 - 16, left: x + 4 }}
                  >
                    {row.label}
                  </div>
                </div>
              )
            }

            // === NOTE / BOX ===
            if (row.type === 'note') {
              const span = row.span || 1
              const x1 = cx(row.col) - COL_W * 0.35
              const w = span > 1 ? (cx(row.col + span - 1) - cx(row.col) + COL_W * 0.7) : COL_W * 0.7
              const styles: Record<string, string> = {
                info: 'bg-blue-50 border-blue-200 text-blue-800',
                error: 'bg-red-50 border-red-200 text-red-700',
                warning: 'bg-amber-50 border-amber-200 text-amber-800',
                alt: 'bg-gray-50 border-gray-300 text-gray-700',
              }
              return (
                <div key={ri} className="absolute flex items-center" style={{ top: y - 14, left: x1, width: w, height: 28 }}>
                  <div className={`w-full text-center text-xs font-semibold px-2 py-1 rounded border ${styles[row.variant || 'info']}`}>
                    {row.label}
                  </div>
                </div>
              )
            }

            // === DIVIDER (alt/error section separator) ===
            if (row.type === 'divider') {
              const variantStyles: Record<string, { bg: string; border: string; text: string }> = {
                alt: { bg: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-600' },
                error: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-600' },
              }
              const s = variantStyles[row.variant || 'alt']
              return (
                <div key={ri} className="absolute flex items-center" style={{ top: y - ROW_H / 2, height: ROW_H, left: PAD_X / 2, right: PAD_X / 2 }}>
                  <div className={`w-full border-t-2 border-dashed ${s.border} relative`}>
                    <span className={`absolute -top-3 left-3 text-xs font-bold px-2 py-0.5 rounded ${s.bg} ${s.text} border ${s.border}`}>
                      {row.label}
                    </span>
                  </div>
                </div>
              )
            }

            return null
          })}
        </div>
      </div>
    </div>
  )
}

export default function ApiGuidePage() {
  const [trainingTab, setTrainingTab] = useState<'text' | 'urls' | 'files'>('text')
  const [sdkLang, setSdkLang] = useState<'javascript' | 'python' | 'php' | 'swift'>('javascript')

  const BASE_URL = API_BASE_URL

  return (
    <div className="max-w-4xl mx-auto space-y-8 p-6">
      {/* Hero */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8 border border-blue-100">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <BookOpen className="w-7 h-7 text-blue-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Build Custom AI Chatbots via API</h1>
            <p className="mt-2 text-gray-600 leading-relaxed">
              Use the CleverAct AI API to create AI chatbots trained on your users' data.
              Build headless integrations where each of your users gets a personalized
              AI assistant — no widget needed.
            </p>
            <div className="mt-4 flex gap-3">
              <Link
                to="/dashboard/developers/api-keys"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <Key className="w-4 h-4" />
                Get API Key
              </Link>
              <a
                href={`${API_BASE_URL}/developers/api`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg text-sm font-medium border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Full API Reference
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Flow diagram */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">How It Works</h2>
        <div className="grid grid-cols-5 gap-2">
          {[
            { icon: Key, label: 'Get API Key', color: 'bg-purple-50 text-purple-700' },
            { icon: Bot, label: 'Create Bot', color: 'bg-blue-50 text-blue-700' },
            { icon: Database, label: 'Train Data', color: 'bg-green-50 text-green-700' },
            { icon: MessageCircle, label: 'Start Session', color: 'bg-orange-50 text-orange-700' },
            { icon: Send, label: 'Send Messages', color: 'bg-pink-50 text-pink-700' }
          ].map(({ icon: StepIcon, label, color }, i) => (
            <div key={label} className="flex flex-col items-center text-center relative">
              <div className={`p-3 rounded-lg ${color}`}>
                <StepIcon className="w-5 h-5" />
              </div>
              <span className="mt-2 text-xs font-medium text-gray-700">{label}</span>
              {i < 4 && (
                <ArrowRight className="absolute right-[-12px] top-3 w-4 h-4 text-gray-300 hidden lg:block" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Architecture: What Happens Behind the Scenes */}
      <Section title="What Happens Behind the Scenes" icon={GitBranch} defaultOpen={false}>
        <p className="text-gray-600 mb-5">
          Here's what happens internally when you train your bot and when users send messages.
        </p>

        {/* Training Flow Sequence Diagram */}
        <div className="mb-8">
          <h4 className="font-semibold text-gray-900 mb-1">Training Flow (All Types)</h4>
          <p className="text-sm text-gray-500 mb-4">All training requests (text, URLs, files) are queued and processed asynchronously:</p>
          <SequenceDiagram
            actors={['Your Server', 'CleverAct AI API', 'Queue Worker', 'Vector DB']}
            rows={[
              { type: 'arrow', from: 0, to: 1, label: 'POST /training/text (or /urls, /files)', color: 'blue' },
              { type: 'arrow', from: 1, to: 0, label: '202 { session_id, status: "queued" }', dashed: true, color: 'amber' },
              { type: 'arrow', from: 1, to: 2, label: 'Enqueue task', color: 'green' },
              { type: 'self', col: 2, label: 'Parse, chunk, embed (OpenAI)', color: 'purple' },
              { type: 'arrow', from: 2, to: 3, label: 'Store vectors + metadata', color: 'orange' },
              { type: 'arrow', from: 3, to: 2, label: 'Confirmed', dashed: true, color: 'gray' },
              { type: 'note', col: 0, label: 'Poll GET /training/status until done', variant: 'info' },
              { type: 'arrow', from: 0, to: 1, label: 'GET /training/status', color: 'blue' },
              { type: 'arrow', from: 1, to: 0, label: '{ pending: 0, processing: 1 }', dashed: true, color: 'amber' },
              { type: 'note', col: 0, label: 'Wait 2s, poll again...', variant: 'alt' },
              { type: 'arrow', from: 0, to: 1, label: 'GET /training/status', color: 'blue' },
              { type: 'arrow', from: 1, to: 0, label: '{ pending: 0, completed: 1, is_active: false }', dashed: true, color: 'green' },
              { type: 'divider', label: 'If error occurs during processing', variant: 'error' },
              { type: 'arrow', from: 1, to: 0, label: '{ failed: 1, files: [{ status: "failed", error }] }', dashed: true, color: 'red' },
            ]}
          />
        </div>

        {/* Concurrency */}
        <div className="mb-8">
          <h4 className="font-semibold text-gray-900 mb-1">Queue & Concurrency</h4>
          <p className="text-sm text-gray-500 mb-4">Multiple requests are queued and processed concurrently with per-user limits:</p>
          <SequenceDiagram
            actors={['Your Server', 'CleverAct AI API', 'Queue (4 workers)']}
            rows={[
              { type: 'arrow', from: 0, to: 1, label: 'POST /training/text (doc A)', color: 'blue' },
              { type: 'arrow', from: 1, to: 0, label: '202 { session_id: "aaa" }', dashed: true, color: 'amber' },
              { type: 'arrow', from: 0, to: 1, label: 'POST /training/urls (doc B)', color: 'blue' },
              { type: 'arrow', from: 1, to: 0, label: '202 { session_id: "bbb" }', dashed: true, color: 'amber' },
              { type: 'arrow', from: 0, to: 1, label: 'POST /training/files (doc C)', color: 'blue' },
              { type: 'arrow', from: 1, to: 0, label: '202 { session_id: "ccc" }', dashed: true, color: 'amber' },
              { type: 'arrow', from: 1, to: 2, label: 'All 3 tasks enqueued', color: 'green' },
              { type: 'note', col: 2, label: 'Max 3 concurrent per user', variant: 'warning' },
              { type: 'self', col: 2, label: 'Process A, B, C in parallel', color: 'purple' },
              { type: 'arrow', from: 0, to: 1, label: 'GET /training/status', color: 'blue' },
              { type: 'arrow', from: 1, to: 0, label: '{ pending: 0, processing: 2, completed: 1 }', dashed: true, color: 'green' },
            ]}
          />
        </div>

        {/* Query Flow Sequence Diagram */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-1">Chat / Query Flow</h4>
          <p className="text-sm text-gray-500 mb-4">What happens when a user sends a message:</p>
          <SequenceDiagram
            actors={['Your App', 'CleverAct AI API', 'Vector DB', 'AI Model', 'Database']}
            rows={[
              { type: 'arrow', from: 0, to: 1, label: 'POST /messages { session_id, message }', color: 'blue' },
              { type: 'self', col: 1, label: 'Load session (get user_id)', color: 'gray' },
              { type: 'arrow', from: 1, to: 2, label: 'Search by query + user_id filter', color: 'green' },
              { type: 'arrow', from: 2, to: 1, label: 'Top matching chunks', dashed: true, color: 'green' },
              { type: 'arrow', from: 1, to: 3, label: 'System prompt + context + question', color: 'purple' },
              { type: 'arrow', from: 3, to: 1, label: 'AI generated response', dashed: true, color: 'purple' },
              { type: 'arrow', from: 1, to: 4, label: 'Save to conversation history', color: 'orange' },
              { type: 'arrow', from: 1, to: 0, label: '{ content: "...", confidence: 0.92 }', dashed: true, color: 'amber' },
              { type: 'divider', label: 'If no matching data found', variant: 'error' },
              { type: 'arrow', from: 1, to: 0, label: '{ content: "I don\'t have info on that", confidence: 0.1 }', dashed: true, color: 'red' },
              { type: 'divider', label: 'If AI model fails', variant: 'error' },
              { type: 'arrow', from: 1, to: 0, label: '500 { detail: "Service temporarily unavailable" }', dashed: true, color: 'red' },
            ]}
          />
        </div>

        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mt-4">
          <h4 className="font-semibold text-indigo-900 mb-1">Key Takeaway</h4>
          <p className="text-sm text-indigo-800">
            The <code className="bg-indigo-100 px-1 rounded font-mono">user_id</code> you set during training
            flows all the way through to query time. When a session has <code className="bg-indigo-100 px-1 rounded font-mono">variables.user_id = "alice"</code>,
            the vector search only returns chunks tagged with that user — ensuring complete data isolation.
          </p>
        </div>
      </Section>

      {/* Step 1: API Key */}
      <Section title="Get Your API Key" icon={Key} step={1} defaultOpen={true}>
        <p className="text-gray-600">
          Create an API key from the{' '}
          <Link to="/dashboard/developers/api-keys" className="text-blue-600 hover:underline font-medium">
            API Keys page
          </Link>
          . You'll need these scopes:
        </p>
        <div className="grid grid-cols-2 gap-2 mt-3">
          {[
            { scope: 'chatbots:write', desc: 'Create and manage bots' },
            { scope: 'training:write', desc: 'Upload training data' },
            { scope: 'training:read', desc: 'Check training status' },
            { scope: 'analytics:read', desc: 'View usage stats' }
          ].map(({ scope, desc }) => (
            <div key={scope} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <code className="text-xs font-mono bg-gray-200 px-1.5 py-0.5 rounded text-gray-800">{scope}</code>
              <span className="text-sm text-gray-600">{desc}</span>
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-500 mt-3">
          All authenticated requests use the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">X-API-Key</code> header.
        </p>
      </Section>

      {/* Step 2: Create Bot */}
      <Section title="Create a Chatbot" icon={Bot} step={2}>
        <p className="text-gray-600 mb-3">
          Create a custom bot. Set <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">bot_type</code> to{' '}
          <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">"custom"</code> for API-first use cases.
        </p>
        <CodeBlock
          code={`curl -X POST ${BASE_URL}/v1/chatbots \\
  -H "X-API-Key: ak_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "My Custom Bot",
    "bot_type": "custom",
    "description": "AI assistant for my app users",
    "system_prompt": "You are a helpful assistant. Answer questions based on the provided context.",
    "temperature": 0.7,
    "max_tokens": 500
  }'`}
        />
        <p className="text-sm text-gray-500 mt-2">Response:</p>
        <CodeBlock
          language="json"
          code={`{
  "id": "cb_1a2b3c4d-5e6f-7890-abcd-ef1234567890",
  "name": "My Custom Bot",
  "bot_type": "custom",
  "collection_name": "user_xxx_chatbot_cb_xxx_conversations",
  "created_at": "2025-08-01T12:00:00Z"
}`}
        />
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
          <p className="text-sm text-blue-800">
            Save the <code className="font-mono bg-blue-100 px-1 rounded">id</code> — you'll use it for training and sessions.
          </p>
        </div>
      </Section>

      {/* Step 3: Train with Data */}
      <Section title="Train with Data" icon={Database} step={3}>
        <p className="text-gray-600 mb-3">
          Feed your bot training data. Include{' '}
          <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">custom_metadata.user_id</code>{' '}
          to isolate data per user.
        </p>

        {/* Sub-tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
          {[
            { key: 'text' as const, label: 'Text', icon: FileText },
            { key: 'urls' as const, label: 'URLs', icon: Globe },
            { key: 'files' as const, label: 'Files', icon: Upload }
          ].map(({ key, label, icon: TabIcon }) => (
            <button
              key={key}
              onClick={() => setTrainingTab(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                trainingTab === key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <TabIcon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {trainingTab === 'text' && (
          <div className="space-y-3 mt-3">
            <p className="text-sm text-gray-600">Send text content. Returns <code className="bg-gray-100 px-1 rounded text-xs">202 Accepted</code> with a <code className="bg-gray-100 px-1 rounded text-xs">session_id</code> to track progress:</p>
            <CodeBlock
              code={`curl -X POST ${BASE_URL}/v1/chatbots/{chatbot_id}/training/text \\
  -H "X-API-Key: ak_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "User Knowledge Base",
    "content": "Your text content here. Can be FAQs, docs, notes, etc.",
    "custom_metadata": {
      "user_id": "alice",
      "category": "onboarding"
    }
  }'`}
            />
            <p className="text-sm text-gray-500">Response (202) — includes <code className="bg-gray-100 px-1 rounded text-xs">data_source_ids</code> immediately:</p>
            <CodeBlock language="json" code={`{
  "session_id": "txt_a1b2c3d4e5f6",
  "chatbot_id": "cb_123",
  "status": "queued",
  "message": "Training queued for background processing",
  "data_source_ids": ["ds_abc123"]
}`} />
          </div>
        )}

        {trainingTab === 'urls' && (
          <div className="space-y-3 mt-3">
            <p className="text-sm text-gray-600">Scrape and train from web pages. Returns <code className="bg-gray-100 px-1 rounded text-xs">202 Accepted</code>:</p>
            <CodeBlock
              code={`curl -X POST ${BASE_URL}/v1/chatbots/{chatbot_id}/training/urls \\
  -H "X-API-Key: ak_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "urls": [
      "https://docs.example.com/getting-started",
      "https://docs.example.com/api-reference"
    ],
    "mode": "individual",
    "custom_metadata": {
      "user_id": "alice",
      "doc_type": "technical"
    }
  }'`}
            />
            <p className="text-sm text-gray-500">Response (202):</p>
            <CodeBlock language="json" code={`{
  "session_id": "url_a1b2c3d4e5f6",
  "chatbot_id": "cb_123",
  "status": "queued",
  "message": "URL training queued for background processing",
  "urls_queued": 2
}`} />
            <p className="text-sm text-gray-500">
              Set <code className="bg-gray-100 px-1 rounded">mode</code> to{' '}
              <code className="bg-gray-100 px-1 rounded">"sublinks"</code> to also crawl internal links from each URL.
            </p>
          </div>
        )}

        {trainingTab === 'files' && (
          <div className="space-y-3 mt-3">
            <p className="text-sm text-gray-600">Upload files (PDF, TXT, CSV, JSON — max 50MB each). Returns <code className="bg-gray-100 px-1 rounded text-xs">202 Accepted</code>:</p>
            <CodeBlock
              code={`curl -X POST ${BASE_URL}/v1/chatbots/{chatbot_id}/training/files \\
  -H "X-API-Key: ak_live_YOUR_KEY" \\
  -F "files=@document.pdf" \\
  -F "files=@faq.txt" \\
  -F 'custom_metadata={"user_id":"alice","source":"upload"}'`}
            />
            <p className="text-sm text-gray-500">Response (202):</p>
            <CodeBlock language="json" code={`{
  "session_id": "upl_a1b2c3d4e5f6",
  "chatbot_id": "cb_123",
  "status": "queued",
  "files_accepted": 2,
  "files_rejected": 0
}`} />
          </div>
        )}

        <div className="mt-4 space-y-3">
          <p className="text-sm font-medium text-gray-700">Poll training status — all active sessions:</p>
          <CodeBlock
            code={`curl ${BASE_URL}/v1/chatbots/{chatbot_id}/training/status \\
  -H "X-API-Key: ak_live_YOUR_KEY"`}
          />
          <p className="text-sm text-gray-500">Response — aggregate counts + per-file breakdown with <code className="bg-gray-100 px-1 rounded text-xs">data_source_id</code>:</p>
          <CodeBlock
            language="json"
            code={`{
  "chatbot_id": "cb_123",
  "chatbot_name": "My Custom Bot",
  "pending": 1,
  "processing": 1,
  "completed": 1,
  "failed": 0,
  "is_active": true,
  "total_files": 3,
  "files": [
    { "data_source_id": "ds_abc123", "filename": "User FAQ", "status": "completed", "chunks_stored": 18 },
    { "filename": "guide.pdf", "status": "processing", "chunks_stored": 0 },
    { "filename": "notes.txt", "status": "pending", "chunks_stored": 0 }
  ]
}`}
          />
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-sm text-gray-700">
              <strong>Done when:</strong>{' '}
              <code className="bg-gray-200 px-1 rounded font-mono text-xs">is_active === false</code> and{' '}
              <code className="bg-gray-200 px-1 rounded font-mono text-xs">pending === 0</code> and{' '}
              <code className="bg-gray-200 px-1 rounded font-mono text-xs">processing === 0</code>.
              Poll every 2-3 seconds.
            </p>
          </div>

          <div className="border-t border-gray-200 pt-3 mt-3">
            <p className="text-sm font-medium text-gray-700">Check a specific training session by <code className="bg-gray-100 px-1 rounded text-xs">session_id</code>:</p>
            <CodeBlock
              code={`curl ${BASE_URL}/v1/chatbots/{chatbot_id}/training/status/{session_id} \\
  -H "X-API-Key: ak_live_YOUR_KEY"`}
            />
            <p className="text-sm text-gray-500">Response — status for that specific session with <code className="bg-gray-100 px-1 rounded text-xs">data_source_ids</code>:</p>
            <CodeBlock
              language="json"
              code={`{
  "session_id": "txt_a1b2c3d4e5f6",
  "chatbot_id": "cb_123",
  "status": "completed",
  "completed": true,
  "pending": 0,
  "processing": 0,
  "completed_count": 1,
  "failed": 0,
  "data_source_ids": ["ds_abc123"],
  "files": [
    { "data_source_id": "ds_abc123", "filename": "User FAQ", "status": "completed", "chunks_stored": 18 }
  ]
}`}
            />
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
              <p className="text-sm text-blue-800">
                Use the <code className="bg-blue-100 px-1 rounded font-mono text-xs">session_id</code> returned from
                any training endpoint to track that specific request.
                Once completed, the response includes <code className="bg-blue-100 px-1 rounded font-mono text-xs">data_source_ids</code> — save
                these to manage (list/delete) your training data later via{' '}
                <code className="bg-blue-100 px-1 rounded font-mono text-xs">DELETE /v1/data-sources/{'{id}'}</code>.
              </p>
            </div>
          </div>
        </div>

        {/* Important training behavior notes */}
        <div className="mt-5 space-y-3">
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <h4 className="text-sm font-semibold text-green-900 mb-1">All training is async (queue-based)</h4>
            <p className="text-sm text-green-800">
              All training endpoints (text, URLs, files) return{' '}
              <code className="bg-green-100 px-1 rounded font-mono text-xs">202 Accepted</code> immediately
              with a <code className="bg-green-100 px-1 rounded font-mono text-xs">session_id</code>.
              Tasks are queued and processed by background workers.
              Poll <code className="bg-green-100 px-1 rounded font-mono text-xs">GET /training/status</code> to track progress.
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <h4 className="text-sm font-semibold text-gray-900 mb-1">Concurrency limits</h4>
            <div className="text-sm text-gray-700 space-y-1">
              <p><strong>Per user:</strong> Up to <strong>3 tasks</strong> process concurrently. Additional requests queue automatically.</p>
              <p><strong>Per upload session:</strong> Up to <strong>3 files</strong> process in parallel within a single file upload.</p>
              <p><strong>System-wide:</strong> 4 worker coroutines handle the global task queue.</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <h4 className="text-sm font-semibold text-blue-900 mb-1">Parallel training example</h4>
            <p className="text-sm text-blue-800 mb-2">
              Send multiple requests in parallel — they'll be queued and processed concurrently.
              Poll status to track all of them:
            </p>
            <CodeBlock
              code={`import requests, time, concurrent.futures

api_key = "ak_live_YOUR_KEY"
bot_id  = "cb_YOUR_BOT_ID"
headers = {"X-API-Key": api_key, "Content-Type": "application/json"}

# 1. Fire off multiple training requests (all return 202 instantly)
def train_doc(doc):
    resp = requests.post(
        f"${BASE_URL}/v1/chatbots/{bot_id}/training/text",
        headers=headers,
        json={
            "title": doc["title"],
            "content": doc["content"],
            "custom_metadata": {"user_id": "alice"}
        }
    )
    return resp.json()  # { session_id, status: "queued" }

with concurrent.futures.ThreadPoolExecutor(max_workers=5) as pool:
    results = list(pool.map(train_doc, documents))
    print(f"Queued {len(results)} tasks")

# 2. Poll until all done
while True:
    status = requests.get(
        f"${BASE_URL}/v1/chatbots/{bot_id}/training/status",
        headers=headers
    ).json()
    print(f"Pending: {status['pending']}, Processing: {status['processing']}, "
          f"Completed: {status['completed']}")
    if not status["is_active"] and status["pending"] == 0 and status["processing"] == 0:
        break
    time.sleep(2)

print("All training complete!")`}
            />
          </div>
        </div>
      </Section>

      {/* Step 4: Start Session */}
      <Section title="Start a Chat Session" icon={MessageCircle} step={4}>
        <p className="text-gray-600 mb-3">
          Create a session for each user conversation. Pass{' '}
          <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">customer_metadata.variables</code>{' '}
          to filter training data and track users in analytics.
        </p>
        <CodeBlock
          code={`curl -X POST ${BASE_URL}/v1/sessions \\
  -H "Content-Type: application/json" \\
  -d '{
    "chatbot_id": "cb_YOUR_BOT_ID",
    "visitor_name": "Alice Smith",
    "customer_metadata": {
      "variables": {
        "user_id": "alice",
        "email": "alice@example.com"
      }
    }
  }'`}
        />
        <p className="text-sm text-gray-500 mt-2">Response:</p>
        <CodeBlock
          language="json"
          code={`{
  "session_id": "sess_abc123",
  "conversation_id": "conv_def456",
  "visitor_id": "alice",
  "status": "active",
  "is_new_conversation": true
}`}
        />
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
          <p className="text-sm text-amber-800">
            Using the same <code className="font-mono bg-amber-100 px-1 rounded">visitor_id</code> will
            resume the existing conversation instead of creating a new one.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-3">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">Identity Variable & visitor_id</h4>
          <p className="text-sm text-blue-800 mb-2">
            You can set <code className="font-mono bg-blue-100 px-1 rounded">visitor_id</code> explicitly,
            or let it be derived automatically from your variables.
          </p>
          <p className="text-sm text-blue-800 mb-2">
            In your bot settings, mark one variable as the <strong>Identity</strong> key (fingerprint icon).
            When no <code className="font-mono bg-blue-100 px-1 rounded">visitor_id</code> is provided in
            the request, the value of that variable is used as the visitor identity. This powers:
          </p>
          <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
            <li>Conversation continuity (same identity = resume conversation)</li>
            <li>Analytics tracking (Unique Users, Returning, New in the Stats modal)</li>
          </ul>
          <p className="text-sm text-blue-700 mt-2">
            <strong>Example:</strong> If <code className="font-mono bg-blue-100 px-1 rounded">user_id</code> is
            marked as identity and you send <code className="font-mono bg-blue-100 px-1 rounded">{`"user_id": "alice"`}</code>,
            the session's <code className="font-mono bg-blue-100 px-1 rounded">visitor_id</code> becomes{' '}
            <code className="font-mono bg-blue-100 px-1 rounded">"alice"</code> automatically.
          </p>
        </div>
      </Section>

      {/* Step 5: Send Messages */}
      <Section title="Send Messages & Get AI Responses" icon={Send} step={5}>
        <p className="text-gray-600 mb-3">
          Send a user message and get a streaming AI response via <strong>Server-Sent Events</strong> (token-by-token):
        </p>
          <CodeBlock
            code={`curl -N -X POST ${BASE_URL}/v1/messages/stream \\
  -H "Content-Type: application/json" \\
  -d '{
    "session_id": "sess_abc123",
    "message": "How do I reset my password?"
  }'`}
          />
          <p className="text-sm text-gray-500 mt-2">Stream events:</p>
          <CodeBlock
            language="json"
            code={`data: {"type":"token","content":"To"}
data: {"type":"token","content":" reset"}
data: {"type":"token","content":" your"}
data: {"type":"token","content":" password"}
...
data: {"type":"message_id","id":"msg_abc123"}
data: {"type":"finish","usage":{"input":150,"output":42}}`}
          />
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
            <p className="text-sm text-blue-800">
              <strong>Event types:</strong>{' '}
              <code className="bg-blue-100 px-1 rounded font-mono text-xs">token</code> — text chunk,{' '}
              <code className="bg-blue-100 px-1 rounded font-mono text-xs">message_id</code> — saved message UUID,{' '}
              <code className="bg-blue-100 px-1 rounded font-mono text-xs">finish</code> — stream complete with usage stats,{' '}
              <code className="bg-blue-100 px-1 rounded font-mono text-xs">error</code> — processing error.
            </p>
          </div>

          <div className="mt-3">
            <p className="text-sm font-medium text-gray-700 mb-2">JavaScript example (EventSource):</p>
            <CodeBlock
              language="javascript"
              code={`const response = await fetch("${BASE_URL}/v1/messages/stream", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    session_id: "sess_abc123",
    message: "How do I reset my password?"
  })
});

const reader = response.body.getReader();
const decoder = new TextDecoder();
let fullText = "";

while (true) {
  const { done, value } = await reader.read();
  if (done) break;

  const chunk = decoder.decode(value);
  for (const line of chunk.split("\\n")) {
    if (!line.startsWith("data: ")) continue;
    const event = JSON.parse(line.slice(6));

    if (event.type === "token") {
      fullText += event.content;
      // Update UI incrementally
    } else if (event.type === "finish") {
      console.log("Done!", event.usage);
    }
  }
}`}
            />
          </div>

        <div className="mt-4 space-y-2">
          <p className="text-sm font-medium text-gray-700">Retrieve conversation history:</p>
          <CodeBlock
            code={`curl ${BASE_URL}/v1/sessions/{session_id}/messages?limit=50`}
          />
        </div>
      </Section>

      {/* Multi-User Isolation */}
      <Section title="Multi-User Data Isolation" icon={Users} step={6} defaultOpen={false}>
        <p className="text-gray-600 mb-3">
          The key to building per-user chatbots is data isolation. Here's how it works:
        </p>

        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="font-semibold text-amber-900 mb-2">0. Configure required variables on the bot</h4>
            <p className="text-sm text-amber-800 mb-2">
              First, add <code className="bg-amber-100 px-1 rounded font-mono">user_id</code> as a <strong>required variable</strong> in
              your bot's settings. This ensures every training upload and chat session must include
              a <code className="bg-amber-100 px-1 rounded font-mono">user_id</code> — requests without it will be rejected with a 400 error.
            </p>
            <CodeBlock
              code={`curl -X PUT ${BASE_URL}/v1/chatbots/{chatbot_id} \\
  -H "X-API-Key: ak_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "settings": {
      "variables": {
        "user_id": {
          "required": true,
          "auto_filter": true,
          "auto_train": true,
          "identity": true,
          "default": ""
        }
      }
    }
  }'`}
            />
            <div className="mt-3 text-xs text-amber-700 space-y-1">
              <p><strong>required</strong> — Reject requests missing this variable (400 error)</p>
              <p><strong>auto_filter</strong> — Automatically filter vector search results by this variable at query time</p>
              <p><strong>auto_train</strong> — Automatically tag training data chunks with this variable's value</p>
              <p><strong>identity</strong> — Use this variable as the user identity for analytics and conversation continuity (only one per bot)</p>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 mb-2">1. Tag training data with user_id</h4>
            <p className="text-sm text-green-800 mb-2">
              When you train your bot, include <code className="bg-green-100 px-1 rounded font-mono">custom_metadata.user_id</code> to
              associate data with a specific user. Since <code className="bg-green-100 px-1 rounded font-mono">user_id</code> is required,
              omitting it will return a 400 error:
            </p>
            <CodeBlock
              code={`// Train data for Alice
POST /v1/chatbots/{id}/training/text
{
  "title": "Alice's Notes",
  "content": "Alice's specific knowledge...",
  "custom_metadata": { "user_id": "alice" }
}

// Train data for Bob
POST /v1/chatbots/{id}/training/text
{
  "title": "Bob's Notes",
  "content": "Bob's specific knowledge...",
  "custom_metadata": { "user_id": "bob" }
}

// ❌ This will fail (400) — user_id is required
POST /v1/chatbots/{id}/training/text
{
  "title": "Orphan data",
  "content": "No user_id provided..."
}`}
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">2. Pass user_id when starting a session</h4>
            <p className="text-sm text-blue-800 mb-2">
              Include the same <code className="bg-blue-100 px-1 rounded font-mono">user_id</code> in session variables.
              The AI will only search training data tagged with that user:
            </p>
            <CodeBlock
              code={`// Session for Alice — only sees Alice's data
POST /v1/sessions
{
  "chatbot_id": "cb_xxx",
  "visitor_id": "alice",
  "customer_metadata": {
    "variables": { "user_id": "alice" }
  }
}

// ❌ This will fail (400) — user_id is required
POST /v1/sessions
{
  "chatbot_id": "cb_xxx",
  "visitor_id": "anonymous"
}`}
            />
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">Result</h4>
            <p className="text-sm text-gray-700">
              One bot, many users. Alice only gets answers from her data. Bob only gets answers from his.
              No data leaks between users. Scale to thousands of users on a single bot.
            </p>
          </div>
        </div>
      </Section>

      {/* Endpoint Reference */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Endpoint Reference</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Method</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Endpoint</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Auth</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Purpose</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[
                ['POST', '/v1/chatbots', 'API Key', 'Create a new chatbot'],
                ['GET', '/v1/chatbots', 'API Key', 'List your chatbots'],
                ['GET', '/v1/chatbots/{id}', 'API Key', 'Get chatbot details'],
                ['PUT', '/v1/chatbots/{id}', 'API Key', 'Update chatbot settings'],
                ['DELETE', '/v1/chatbots/{id}', 'API Key', 'Delete a chatbot'],
                ['POST', '/v1/chatbots/{id}/training/text', 'API Key', 'Train with text (returns 202)'],
                ['POST', '/v1/chatbots/{id}/training/urls', 'API Key', 'Train with URLs (returns 202)'],
                ['POST', '/v1/chatbots/{id}/training/files', 'API Key', 'Train with files (returns 202)'],
                ['POST', '/v1/chatbots/{id}/training/files/base64', 'API Key', 'Train with base64-encoded files'],
                ['GET', '/v1/chatbots/{id}/training/status', 'API Key', 'Poll training status (all sessions)'],
                ['GET', '/v1/chatbots/{id}/training/status/{session_id}', 'API Key', 'Check specific training session'],
                ['GET', '/v1/chatbots/{id}/data-sources', 'API Key', 'List training data sources'],
                ['PATCH', '/v1/data-sources/{id}', 'API Key', 'Update data source metadata'],
                ['DELETE', '/v1/data-sources/{id}', 'API Key', 'Delete a training data source'],
                ['POST', '/v1/sessions', 'None', 'Start a chat session'],
                ['POST', '/v1/messages/stream', 'None', 'Send message & stream AI response (SSE)'],
                ['GET', '/v1/sessions/{id}/messages', 'None', 'Get conversation history'],
                ['GET', '/v1/usage', 'API Key', 'Check usage & plan limits']
              ].map(([method, endpoint, auth, purpose], i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${
                        method === 'GET'
                          ? 'bg-green-100 text-green-700'
                          : method === 'POST'
                          ? 'bg-blue-100 text-blue-700'
                          : method === 'PUT'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {method}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-800">{endpoint}</td>
                  <td className="px-4 py-2.5 text-gray-600">{auth}</td>
                  <td className="px-4 py-2.5 text-gray-600">{purpose}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Status Codes & Errors */}
      <Section title="Status Codes & Errors" icon={AlertTriangle}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Meaning</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Fix</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[
                ['202', 'Training request accepted & queued', 'Not an error — poll GET /training/status for progress'],
                ['401', 'Invalid or missing API key', 'Check X-API-Key header value'],
                ['403', 'API key missing required scope', 'Add the required scope to your API key'],
                ['404', 'Chatbot or session not found', 'Verify the ID exists and belongs to your account'],
                ['409', 'Idempotency key conflict', 'Use a different Idempotency-Key header value'],
                ['422', 'Invalid request body', 'Check required fields and data types'],
                ['429', 'Rate limit exceeded', 'Reduce request frequency or wait and retry'],
                ['402', 'Plan limit reached', 'Upgrade your plan for higher limits']
              ].map(([status, meaning, fix], i) => (
                <tr key={i}>
                  <td className="px-4 py-2.5">
                    <code className={`px-1.5 py-0.5 rounded text-xs font-mono ${
                      status === '202' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}>{status}</code>
                  </td>
                  <td className="px-4 py-2.5 text-gray-800">{meaning}</td>
                  <td className="px-4 py-2.5 text-gray-600">{fix}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* SDK Integration Examples */}
      <Section title="SDK Integration Examples" icon={Code} defaultOpen={false}>
        <p className="text-gray-600 mb-4">
          Complete integration examples in popular languages. Each example creates a bot, trains it,
          starts a session, and sends a message — end to end.
        </p>

        {/* Language tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit mb-4">
          {([
            { key: 'javascript' as const, label: 'JavaScript' },
            { key: 'python' as const, label: 'Python' },
            { key: 'php' as const, label: 'PHP' },
            { key: 'swift' as const, label: 'Swift' },
          ]).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setSdkLang(key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                sdkLang === key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {sdkLang === 'javascript' && (
          <CodeBlock
            language="javascript"
            code={`const API_KEY = "ak_live_YOUR_KEY";
const BASE    = "${BASE_URL}/v1";
const headers = { "X-API-Key": API_KEY, "Content-Type": "application/json" };

async function main() {
  // 1. Create a chatbot
  const bot = await fetch(\`\${BASE}/chatbots\`, {
    method: "POST", headers,
    body: JSON.stringify({
      name: "My Bot", bot_type: "custom",
      settings: {
        variables: {
          user_id: { required: true, auto_filter: true, auto_train: true, identity: true }
        }
      }
    })
  }).then(r => r.json());
  const botId = bot.id;
  console.log("Created bot:", botId);

  // 2. Train with text (returns 202 — async)
  const train = await fetch(\`\${BASE}/chatbots/\${botId}/training/text\`, {
    method: "POST", headers,
    body: JSON.stringify({
      title: "FAQ",
      content: "To reset your password, go to Settings > Security > Reset Password.",
      custom_metadata: { user_id: "alice" }
    })
  }).then(r => r.json());
  console.log("Queued:", train.session_id);

  // 3. Poll until training completes
  while (true) {
    const status = await fetch(\`\${BASE}/chatbots/\${botId}/training/status\`, { headers })
      .then(r => r.json());
    console.log(\`Pending: \${status.pending}, Processing: \${status.processing}, Completed: \${status.completed}\`);
    if (!status.is_active && status.pending === 0 && status.processing === 0) break;
    await new Promise(r => setTimeout(r, 2000));
  }
  console.log("Training complete!");

  // 4. Start a chat session (no API key needed)
  const session = await fetch(\`\${BASE}/sessions\`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      chatbot_id: botId,
      visitor_id: "alice-web",
      customer_metadata: { variables: { user_id: "alice" } }
    })
  }).then(r => r.json());

  // 5. Send a message (streaming — real-time token-by-token)
  const stream = await fetch(\`\${BASE}/messages/stream\`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      session_id: session.session_id,
      message: "Tell me more about security settings"
    })
  });

  const reader = stream.body.getReader();
  const decoder = new TextDecoder();
  let fullText = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    for (const line of decoder.decode(value).split("\\n")) {
      if (!line.startsWith("data: ")) continue;
      const event = JSON.parse(line.slice(6));
      if (event.type === "token") {
        fullText += event.content;
        process.stdout.write(event.content); // print tokens as they arrive
      } else if (event.type === "finish") {
        console.log("\\nDone! Tokens used:", event.usage);
      }
    }
  }
}

main();`}
          />
        )}

        {sdkLang === 'python' && (
          <CodeBlock
            language="python"
            code={`import requests, time

API_KEY = "ak_live_YOUR_KEY"
BASE    = "${BASE_URL}/v1"
headers = {"X-API-Key": API_KEY, "Content-Type": "application/json"}

# 1. Create a chatbot
bot = requests.post(f"{BASE}/chatbots", headers=headers, json={
    "name": "My Bot", "bot_type": "custom",
    "settings": {
        "variables": {
            "user_id": {"required": True, "auto_filter": True, "auto_train": True, "identity": True}
        }
    }
}).json()
bot_id = bot["id"]
print(f"Created bot: {bot_id}")

# 2. Train with text (returns 202 — async)
train = requests.post(f"{BASE}/chatbots/{bot_id}/training/text", headers=headers, json={
    "title": "FAQ",
    "content": "To reset your password, go to Settings > Security > Reset Password.",
    "custom_metadata": {"user_id": "alice"}
}).json()
print(f"Queued: {train['session_id']}")

# 3. Poll until training completes
while True:
    status = requests.get(f"{BASE}/chatbots/{bot_id}/training/status", headers=headers).json()
    print(f"Pending: {status['pending']}, Processing: {status['processing']}, Completed: {status['completed']}")
    if not status["is_active"] and status["pending"] == 0 and status["processing"] == 0:
        break
    time.sleep(2)
print("Training complete!")

# 4. Start a chat session (no API key needed)
session = requests.post(f"{BASE}/sessions", json={
    "chatbot_id": bot_id,
    "visitor_id": "alice-web",
    "customer_metadata": {"variables": {"user_id": "alice"}}
}).json()

# 5. Send a message (streaming — real-time token-by-token)
import json
resp = requests.post(f"{BASE}/messages/stream", json={
    "session_id": session["session_id"],
    "message": "Tell me more about security settings"
}, stream=True)

for line in resp.iter_lines():
    line = line.decode()
    if not line.startswith("data: "):
        continue
    event = json.loads(line[6:])
    if event["type"] == "token":
        print(event["content"], end="", flush=True)
    elif event["type"] == "finish":
        print(f"\\nDone! Tokens used: {event['usage']}")`}
          />
        )}

        {sdkLang === 'php' && (
          <CodeBlock
            language="php"
            code={`<?php
$API_KEY = "ak_live_YOUR_KEY";
$BASE    = "${BASE_URL}/v1";

function api($method, $url, $body = null, $useKey = true) {
    global $API_KEY;
    $ch = curl_init($url);
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    curl_setopt($ch, CURLOPT_CUSTOMREQUEST, $method);
    $headers = ["Content-Type: application/json"];
    if ($useKey) $headers[] = "X-API-Key: $API_KEY";
    curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
    if ($body) curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($body));
    $resp = curl_exec($ch);
    curl_close($ch);
    return json_decode($resp, true);
}

// 1. Create a chatbot
$bot = api("POST", "$BASE/chatbots", [
    "name" => "My Bot", "bot_type" => "custom",
    "settings" => [
        "variables" => [
            "user_id" => ["required" => true, "auto_filter" => true, "auto_train" => true, "identity" => true]
        ]
    ]
]);
$botId = $bot["id"];
echo "Created bot: $botId\\n";

// 2. Train with text (returns 202 — async)
$train = api("POST", "$BASE/chatbots/$botId/training/text", [
    "title" => "FAQ",
    "content" => "To reset your password, go to Settings > Security > Reset Password.",
    "custom_metadata" => ["user_id" => "alice"]
]);
echo "Queued: {$train['session_id']}\\n";

// 3. Poll until training completes
while (true) {
    $status = api("GET", "$BASE/chatbots/$botId/training/status");
    echo "Pending: {$status['pending']}, Processing: {$status['processing']}, Completed: {$status['completed']}\\n";
    if (!$status["is_active"] && $status["pending"] == 0 && $status["processing"] == 0) break;
    sleep(2);
}
echo "Training complete!\\n";

// 4. Start a chat session (no API key needed)
$session = api("POST", "$BASE/sessions", [
    "chatbot_id" => $botId,
    "visitor_id" => "alice-web",
    "customer_metadata" => ["variables" => ["user_id" => "alice"]]
], false);

// 5. Send a message (streaming — real-time token-by-token)
$ch = curl_init("$BASE/messages/stream");
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, ["Content-Type: application/json"]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
    "session_id" => $session["session_id"],
    "message" => "Tell me more about security settings"
]));
curl_setopt($ch, CURLOPT_WRITEFUNCTION, function($curl, $data) {
    foreach (explode("\\n", $data) as $line) {
        if (strpos($line, "data: ") !== 0) continue;
        $event = json_decode(substr($line, 6), true);
        if ($event["type"] === "token") echo $event["content"];
        elseif ($event["type"] === "finish") echo "\\nDone!\\n";
    }
    return strlen($data);
});
curl_exec($ch);
curl_close($ch);`}
          />
        )}

        {sdkLang === 'swift' && (
          <CodeBlock
            language="swift"
            code={`import Foundation

let apiKey = "ak_live_YOUR_KEY"
let base   = "${BASE_URL}/v1"

func api(_ method: String, _ path: String, body: [String: Any]? = nil, useKey: Bool = true) async throws -> [String: Any] {
    var req = URLRequest(url: URL(string: "\\(base)\\(path)")!)
    req.httpMethod = method
    req.setValue("application/json", forHTTPHeaderField: "Content-Type")
    if useKey { req.setValue(apiKey, forHTTPHeaderField: "X-API-Key") }
    if let body { req.httpBody = try JSONSerialization.data(withJSONObject: body) }
    let (data, _) = try await URLSession.shared.data(for: req)
    return try JSONSerialization.jsonObject(with: data) as! [String: Any]
}

Task {
    // 1. Create a chatbot
    let bot = try await api("POST", "/chatbots", body: [
        "name": "My Bot", "bot_type": "custom",
        "settings": [
            "variables": [
                "user_id": ["required": true, "auto_filter": true, "auto_train": true, "identity": true]
            ]
        ]
    ])
    let botId = bot["id"] as! String
    print("Created bot: \\(botId)")

    // 2. Train with text (returns 202 — async)
    let train = try await api("POST", "/chatbots/\\(botId)/training/text", body: [
        "title": "FAQ",
        "content": "To reset your password, go to Settings > Security > Reset Password.",
        "custom_metadata": ["user_id": "alice"]
    ])
    print("Queued: \\(train["session_id"] ?? "")")

    // 3. Poll until training completes
    while true {
        let status = try await api("GET", "/chatbots/\\(botId)/training/status")
        let pending = status["pending"] as? Int ?? 0
        let processing = status["processing"] as? Int ?? 0
        let completed = status["completed"] as? Int ?? 0
        let isActive = status["is_active"] as? Bool ?? false
        print("Pending: \\(pending), Processing: \\(processing), Completed: \\(completed)")
        if !isActive && pending == 0 && processing == 0 { break }
        try await Task.sleep(nanoseconds: 2_000_000_000)
    }
    print("Training complete!")

    // 4. Start a chat session (no API key needed)
    let session = try await api("POST", "/sessions", body: [
        "chatbot_id": botId,
        "visitor_id": "alice-ios",
        "customer_metadata": ["variables": ["user_id": "alice"]]
    ], useKey: false)
    let sessionId = session["session_id"] as! String

    // 5. Send a message (streaming — real-time token-by-token)
    var streamReq = URLRequest(url: URL(string: "\\(base)/messages/stream")!)
    streamReq.httpMethod = "POST"
    streamReq.setValue("application/json", forHTTPHeaderField: "Content-Type")
    streamReq.httpBody = try JSONSerialization.data(withJSONObject: [
        "session_id": sessionId,
        "message": "Tell me more about security settings"
    ])

    let (bytes, _) = try await URLSession.shared.bytes(for: streamReq)
    for try await line in bytes.lines {
        guard line.hasPrefix("data: ") else { continue }
        let jsonStr = String(line.dropFirst(6))
        guard let data = jsonStr.data(using: .utf8),
              let event = try? JSONSerialization.jsonObject(with: data) as? [String: Any],
              let type = event["type"] as? String else { continue }
        if type == "token", let content = event["content"] as? String {
            print(content, terminator: "")
        } else if type == "finish" {
            print("\\nDone!")
        }
    }
}`}
          />
        )}

        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            All examples follow the same pattern: <strong>create bot → train (202) → poll status → start session → stream</strong>.
            Session and message endpoints don't require an API key, so your client app can call them directly.
            Use <code className="bg-blue-100 px-1 rounded font-mono text-xs">/v1/messages/stream</code> for real-time token-by-token SSE output.
          </p>
        </div>
      </Section>

      {/* Security note */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-5 flex items-start gap-3">
        <Shield className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
        <div>
          <h4 className="font-medium text-gray-900">Security Note</h4>
          <p className="text-sm text-gray-600 mt-1">
            Keep your API key server-side only. Never expose it in frontend code or client-side requests.
            Session and message endpoints don't require an API key, so your frontend can call them directly.
          </p>
        </div>
      </div>
    </div>
  )
}
